/**
 * ActionsPane — product unit query button pane.
 *
 * - Renders a full-width CTA "Send inquiry" button.
 * - Animates appending "about unit {id_original}" when the product heading
 *   ([data-product-heading]) scrolls out of view (IntersectionObserver).
 * - When isSticky=true: bidirectional sticky within .panesArea —
 *     bottom: 0  → sticks to viewport bottom if pane is below the fold
 *     top: --bim-section-padding → sticks to top when scrolled past
 *   White background + white-glow shadow always present when sticky;
 *   dark shadow added on top-stuck detection (sentinel).
 * - On click: dispatches custom event 'cadaplusProductQueryClick' so the
 *   host WP page can wire its own contact-form scroll / mailto logic.
 */

import { useEffect, useRef, useState } from 'react'

import { useApp } from '../../../../app/AppContext.jsx'
import { useProduct } from '../../index.js'

import { useI18n } from '../../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js'

import { Button } from '../../../../shared/ui/Button/index.js'

import styles from './ActionsPane.module.scss'

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Read --bim-section-padding from the app root and return a value
 * that is safe to use as an IntersectionObserver rootMargin (px or % only).
 * Converts rem → px on the fly; passes px/% values through unchanged.
 * Falls back to '64px'.
 */
function getSectionPaddingPx(rootElement) {
  if (!rootElement) return '64px'

  const rootStyles = getComputedStyle(rootElement)
  const raw  = rootStyles.getPropertyValue('--bim-section-padding').trim()

  if (!raw) return '64px'

  // Already in px or %
  if (/px$/.test(raw) || /%$/.test(raw)) return raw

  // Convert rem → px
  if (/rem$/.test(raw)) {
    const remValue = parseFloat(raw)
    const pxPerRem = parseFloat(rootStyles.fontSize) || 16
    return `${Math.round(remValue * pxPerRem)}px`
  }

  // Numeric without unit — treat as px
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}px`

  return '64px'
}

// ── ActionsPane ───────────────────────────────────────────────────────────────

export function ActionsPane({ isSticky = false }) {
  const { containerRef } = useApp()
  const { selection } = useProduct()
  const { t } = useI18n()
  const getProductsCategoryString = useProductStrings()

  const product    = selection.product
  const idOriginal = product?.id_original ?? product?.id_cad ?? null

  const [showUnitNumber, setShowUnitNumber] = useState(false)
  const [isStuck,        setIsStuck]        = useState(false)
  const sentinelRef = useRef(null)

  // ── Heading visibility detection ─────────────────────────────────────────
  // Observe [data-product-heading] — appended unit number fades in when heading
  // scrolls above the viewport top (accounting for sticky navbar).
  useEffect(() => {
    if (!idOriginal) return

    // Defer one tick so the heading has had a chance to mount/render.
    const timer = setTimeout(() => {
      const heading = document.querySelector('[data-product-heading]')
      if (!heading) return

      const padding = getSectionPaddingPx(containerRef.current)
      const observer = new IntersectionObserver(
        ([entry]) => setShowUnitNumber(!entry.isIntersecting),
        { rootMargin: `-${padding} 0px 0px 0px`, threshold: 0 },
      )
      observer.observe(heading)
      return () => observer.disconnect()
    }, 0)

    return () => clearTimeout(timer)
  }, [containerRef, idOriginal])

  // ── Top-sticky detection (shadow trigger) ────────────────────────────────
  // Sentinel sits just before this pane in the DOM flow.
  // When it scrolls past the top threshold (sectionPadding), the pane is
  // stuck to the top → enable dark elevation shadow.
  useEffect(() => {
    if (!isSticky) return
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const padding = getSectionPaddingPx(containerRef.current)
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { rootMargin: `-${padding} 0px 0px 0px`, threshold: 1 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [containerRef, isSticky])

  // ── Click handler ────────────────────────────────────────────────────────
  const handleClick = () => {
    const event = new CustomEvent('cadaplusProductQueryClick', {
      detail: {
        unitId:   product?.crm_unit_id ?? product?.crm_id_unit ?? null,
        unitName: idOriginal,
      },
      bubbles:  true,
      composed: true,
    })
    document.dispatchEvent(event)
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (!product) return null

  return (
    <>
      {/* Sentinel placed before the sticky pane to detect top-stuck state */}
      {isSticky && <div ref={sentinelRef} style={{ height: '1px', width: '100%' }} />}

      <div
        className={[
          styles.actionsPane,
          isSticky ? styles.sticky : null,
          isStuck  ? styles.stuck  : null,
        ].filter(Boolean).join(' ')}
      >
        <Button type="button" kind="wide" size="large" cta onClick={handleClick}>
          <span>{t('common.send_inquiry')}</span>
          <span
            className={styles.unitNumber}
            style={{
              maxWidth:    showUnitNumber ? '200px' : '0',
              paddingLeft: showUnitNumber ? '0.5rem' : '0',
            }}
          >
            {getProductsCategoryString('about', product.product_category, { id: idOriginal })}
          </span>
        </Button>
      </div>
    </>
  )
}
